/*
 * ////////////////////////////////////////////////////////////////////////////////
 * //
 * // This software system consists of computer software and documentation.
 * // It contains trade secrets and confidential information which are proprietary
 * // to Everi Games Inc.  Its use or disclosure in whole or in part without
 * // the express written permission of Everi Games Inc. is prohibited.
 * //
 * // This software system is also an unpublished work protected under the copyright
 * // laws of the United States of America.
 * //
 * // Copyright © 2018 Everi Games Inc.  All Rights Reserved
 * //
 * ////////////////////////////////////////////////////////////////////////////////
 */
import CLog, { LogTypes } from '../Utility/ComponentLogger';
import IUserMessageNodeBuilder from './IUserMessageNodeBuilder';
import UserMessageManager from './UserMessageManager';

/** A builder class for constructing displays for the user message portion of the playbar. */
export default class UserMessageNodeBuilder implements IUserMessageNodeBuilder
{
    /** The event name to use for the BuildFinished event. */
    public static readonly BuildFinishedEventName: string = "UserMessageNodeBuilder.BuildFinished";

    /** Whether or not the current configuration of this builder has been cleared. */
    private _isCleared: boolean = false;
    /** Accessor  */
    public get IsCleared(): boolean { return this._isCleared; }

    /** Whether or not this builder rotates between multiple messages. */
    private _isRotation: boolean = false;

    /** The [[string]] created for display by this builder. */
    private _currentItem: string = "";
    private _currentColor: cc.Color = cc.Color.WHITE;

    /** The [[UserMessageManager]] this builder is constructing displays for. */
    private _parentManager: UserMessageManager = null;

    /** The [[string]] collection representing each display to rotate through. */
    private _rotateItems: string[] = [];

    /** The [[cc.Color]] collection representing each color to rotate through. */
    private _rotateColors: cc.Color[] = [];

    /** How many rotations have occured in the current set of messages. */
    private _rotationCount: number = 0;

    /**
     * Base constructor for a [[UserMessageNodeBuilder]].
     * @param parentManager The [[UserMessageManager]] that this builder will construct displays for.
     */
    constructor(parentManager: UserMessageManager)
    {
        this._parentManager = parentManager;
    }

    /**
     * Adds the queued up items to the layout and begins rotation, if any.
     * @returns This [[UserMessageNodeBuilder]].
     */
    public Build = (): UserMessageNodeBuilder =>
    {
        // Will help settle race conditions and give time for display.
        // In multi-threaded environment this would not be safe.
        if (!this._parentManager.BuildInProgress)
        {
            this._parentManager.BuildInProgress = true;
            this._parentManager.CurrentNodeBuilder = this;
        }
        else
        {
            // Bind and try again..
            this._parentManager.ObservableUserMessage.node
                .once(UserMessageNodeBuilder.BuildFinishedEventName, this.Build);
            return;
        }

        if (!this._parentManager.ObservableUserMessage.node)
        {
            CLog.error(`Trying to build without build node set.`, this._parentManager, LogTypes.UserMessageManager);
            return;
        }

        if (this._isRotation)
        {
            // Cache current rotation for cycle.
            this._rotateItems.push(this._currentItem);
            this._currentItem = this._rotateItems[0];
            this._rotateColors.push(this._currentColor);
            this._currentColor = this._rotateColors[0];
        }

        this.buildContents();

        // Event handling
        this._parentManager.BuildInProgress = false;
        this._parentManager.ObservableUserMessage.node.emit(UserMessageNodeBuilder.BuildFinishedEventName);

        this._isCleared = false;

        return this;
    }

    /**
     * Manually make the user message display rotate to the next message.
     * @returns This [[UserMessageNodeBuilder]].
     */
    public Rotate(): UserMessageNodeBuilder
    {
        if (!this._isRotation || this._isCleared) { return; }

        this._rotationCount = ++this._rotationCount % this._rotateItems.length;
        this._currentItem = this._rotateItems[this._rotationCount];
        this._currentColor = this._rotateColors[this._rotationCount];

        this.buildContents();

        return this;
    }

    /**
     * @returns Is this the current builder being used by the parent [[UserMessageManager]].
     */
    public IsCurrent(): boolean
    {
        return this._parentManager.CurrentNodeBuilder === this;
    }

    // #region Public Message Mutators

    /**
     * Stashes the current item collection and begins a new one for use in rotation.
     * @returns This [[UserMessageNodeBuilder]].
     */
    public NextRotation(): UserMessageNodeBuilder
    {
        this._isRotation = true;

        this._rotateItems.push(this._currentItem);
        this._currentItem = "";
        this._rotateColors.push(this._currentColor);
        this._currentColor = cc.Color.WHITE;

        return this;
    }

    /**
     * Clears all contents of the [[UserMessageNodeBuilder]].
     * @returns This [[UserMessageNodeBuilder]].
     */
    public Clear(): UserMessageNodeBuilder
    {
        this._isCleared = true;
        return this.clearContents();
    }

    /**
     * Adds a reel symbol to the current display.
     * @param symbolID The ID of the symbol to add.
     * @returns This [[UserMessageNodeBuilder]].
     */
    public AddSymbol(symbolID: string): UserMessageNodeBuilder
    {
        // let texture: cc.SpriteFrame = null;

        // // Try to pull symbol is exists.
        // if (this._parentManager.SymbolSet && !this._parentManager.UseSymbolStringValue)
        // {
        //     texture = this._parentManager.SymbolSet.GetSymbolDataById(symbolID).StaticAsset.spriteFrame;
        // }

        // if (texture)
        // {
        //     this.AddTexture(texture);
        // }
        // else
        // {
        //     this.AddText(symbolID);
        // }

        return this;
    }

    /**
     * Adds a texture to current display.
     * @param texture The texture to be added.
     * @returns This [[UserMessageNodeBuilder]].
     */
    public AddTexture(texture: cc.SpriteFrame): UserMessageNodeBuilder
    {
        // let parentSpriteNode: cc.Node = new cc.Node("ParentOfSprite");
        // parentSpriteNode.setContentSize(this._parentManager.SymbolWidth * this._parentManager.SymbolSpacing,
        //     this._parentManager.SymbolWidth);

        // let spriteNode: cc.Node = new cc.Node("Sprite");
        // let sprite: cc.Sprite = spriteNode.addComponent(cc.Sprite);

        // let spriteFrame: cc.SpriteFrame = new cc.SpriteFrame(texture.rawUrl);
        // sprite.spriteFrame = spriteFrame;

        // let scale: number = this._parentManager.SymbolWidth / spriteNode.height;
        // spriteNode.setScale(scale, scale);

        // parentSpriteNode.addChild(spriteNode);

        // this._currentItem.push(parentSpriteNode);

        return this;
    }

    /**
     * Adds a string of text to the current message display.
     * @param message The string of text to display.
     * @returns This [[UserMessageNodeBuilder]].
     */
    public AddText(message: string): UserMessageNodeBuilder
    {
        // let textNode: cc.Node = new cc.Node("Label");

        // let label: cc.Label = textNode.addComponent(cc.Label);
        // label.string = message;
        // label.font = this._parentManager.Font;
        // label.fontSize = this._parentManager.FontSize;
        // label.lineHeight = this._parentManager.LineHeight;

        // textNode.addComponent(AssetScaler);

        this._currentItem = `${this._currentItem}${message}`;

        return this;
    }

    /**
     * Sets the color of the relevant display items.
     * @param color The color to set the item(s).
     * @param itemNumber Which item in the current display to change the color of. -1 sets all items in the display.
     * @returns This [[UserMessageNodeBuilder]].
     */
    public SetColor(color: cc.Color, itemNumber: number = -1): UserMessageNodeBuilder
    {
        this._parentManager.ObservableUserMessageColor.Value = color;
        // if (itemNumber > 0 && itemNumber < this._currentItem.length)
        // {
        //     this._currentItem[itemNumber].color = color;
        //     return;
        // }

        // this._currentItem.forEach((node) => node.color = color);
        return this;
    }

    // #endregion

    /**
     * Helper function for copying the contents of the current display to the layout.
     * @hidden
     */
    private buildContents(): UserMessageNodeBuilder
    {
        this.clearContents();

        this._parentManager.ObservableUserMessage.Value = this._currentItem;
        // Created formatted message.
        // if (this._currentItem.length > 0)
        // {
        //     this._currentItem.forEach((x) => this._parentManager.ObservableUserMessage.node.addChild(x), this);
        // }

        return this;
    }

    /**
     * Clears the current items from the user message display.
     * @hidden
     */
    private clearContents(): UserMessageNodeBuilder
    {
        this._parentManager.ObservableUserMessage.Value = '';

        return this;
    }
}
